import { create } from 'zustand';
import {
  createPlanMaster as apiCreate,
  listPlanMasters as apiList,
  getPlanMaster as apiGet,
  updatePlanMaster as apiUpdate,
  deletePlanMaster as apiDelete,
  attachPlanToVisit as apiAttach,
} from '../services/api/treatmentPlanMasterApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Treatment Plan Master Store (Phase 2.7)
 *
 * Holds workflow plans the caller can see:
 *   - Doctor: own active plans only.
 *   - Admin: all active plans across the org (filterable on the page).
 *
 * Re-fetches on every mutation rather than patching — same convention
 * as procedureStore. The shape mirrors the backend
 * TreatmentPlanMasterResponse: each plan carries plan_name,
 * description, ordering_rule, package_discount, steps[], usage_count,
 * is_active, plus owner / timestamps.
 *
 * `attach` returns the full result including any structured
 * MISSING_ASSIGNEES error so the AttachPlanModal can route to the
 * highlight-and-default-to-me recovery flow without refetching.
 * ═══════════════════════════════════════════════════════════════════════
 */
const useTreatmentPlanMasterStore = create((set, get) => ({
  plans: [],
  isLoading: false,
  error: null,

  fetchPlans: async ({ includeInactive = false } = {}) => {
    set({ isLoading: true, error: null });
    const r = await apiList({ includeInactive });
    if (r.success) {
      set({ plans: r.data?.plans || [], isLoading: false });
    } else {
      set({ error: r.error, isLoading: false });
    }
    return r;
  },

  createPlan: async (body, opts) => {
    const r = await apiCreate(body, opts);
    if (r.success) await get().fetchPlans();
    return r;
  },

  updatePlan: async (planId, body, opts) => {
    const r = await apiUpdate(planId, body, opts);
    if (r.success) await get().fetchPlans();
    return r;
  },

  deletePlan: async (planId, opts) => {
    const r = await apiDelete(planId, opts);
    if (r.success) await get().fetchPlans();
    return r;
  },

  fetchPlan: async (planId, opts) => apiGet(planId, opts),

  /**
   * Attach a workflow master to a patient visit.
   *
   * Returns the API-shaped result `{success, data?, error?, errorCode?,
   * errorPayload?}`. On `errorCode==='MISSING_ASSIGNEES'` callers should
   * keep the modal open and render the offending step rows.
   */
  attachToVisit: async (sourcePlanId, body) => {
    return apiAttach(sourcePlanId, body);
  },

  /**
   * Local helper — find a plan by id from the cached list. Used by the
   * AttachPlanModal once a plan is picked from the dropdown.
   */
  getPlanById: (planId) =>
    get().plans.find((p) => p.plan_id === planId) || null,

  clearError: () => set({ error: null }),
}));

export default useTreatmentPlanMasterStore;
